import { m } from "@nerves/i18n/messages";
import type { TokenView } from "@nerves/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Page, Panel } from "@/components/shell/Page";
import {
  Avatar,
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  CopyField,
  InlineError,
  RelativeTime,
  RowSkeleton,
  SectionHeader,
  TextField,
} from "@/components/ui";
import { api } from "@/lib/api";
import { keys, sessionsQuery, tokensQuery } from "@/lib/queries";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/agents")({ component: AgentsPage });

interface Created {
  token: string;
  installCommand: string;
}

/** Create a token, see the command once, verify with whoami. The secret never shows again. */
function ConnectClaude() {
  const qc = useQueryClient();
  const [label, setLabel] = useState("Claude Code");
  const [created, setCreated] = useState<Created | null>(null);
  const create = useMutation({
    mutationFn: () => api.post<Created>("/api/v1/tokens", { label: label.trim() || "Claude Code" }),
    onSuccess: (res) => {
      setCreated(res);
      void qc.invalidateQueries({ queryKey: keys.tokens });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <section aria-labelledby="connect-title" className="flex flex-col gap-3">
      <SectionHeader id="connect-title" title={m.connect_title()} description={m.connect_body()} />
      <Panel className="flex flex-col gap-4 p-4">
        {created ? (
          <>
            <Callout tone="warning" title={m.connect_copy_title()} role="status">
              {m.connect_copy_body()}
            </Callout>
            <CopyField label={m.connect_command()} value={created.installCommand} multiline />
            <p className="text-sm text-fg-muted">{m.connect_verify()}</p>
            <div>
              <Button onPress={() => setCreated(null)}>{m.connect_another()}</Button>
            </div>
          </>
        ) : (
          <form noValidate onSubmit={submit} className="flex flex-wrap items-end gap-3">
            <TextField
              label={m.connect_label()}
              description={m.connect_label_desc()}
              value={label}
              onChange={setLabel}
              className="min-w-56 flex-1"
            />
            <Button type="submit" variant="primary" isPending={create.isPending}>
              {m.connect_create()}
            </Button>
            {create.isError ? <InlineError className="w-full" message={m.error_generic()} /> : null}
          </form>
        )}
      </Panel>
    </section>
  );
}

function TokenRow({ token, onRevoke }: { token: TokenView; onRevoke: (t: TokenView) => void }) {
  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <Avatar name={token.agent.name} bot size="lg" labelled={false} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{token.label}</span>
        <span className="text-xs text-fg-muted">
          {m.token_agent({ agent: token.agent.name })} · <span className="font-mono">nv_{token.prefix}…</span> ·{" "}
          {token.lastUsedAt ? (
            <>
              {m.token_last_used({ time: "" }).trim()} <RelativeTime value={token.lastUsedAt} />
            </>
          ) : (
            m.token_never_used()
          )}
        </span>
      </div>
      {token.revokedAt ? (
        <Badge>{m.token_revoked()}</Badge>
      ) : (
        <Button size="sm" variant="ghost" onPress={() => onRevoke(token)}>
          {m.token_revoke()}
        </Button>
      )}
    </li>
  );
}

function Tokens() {
  const qc = useQueryClient();
  const query = useQuery(tokensQuery);
  const [revoking, setRevoking] = useState<TokenView | null>(null);
  return (
    <section aria-labelledby="tokens-title" className="flex flex-col gap-3">
      <SectionHeader id="tokens-title" title={m.tokens_title()} />
      <Panel>
        {query.isPending ? (
          <RowSkeleton rows={2} className="p-4" />
        ) : query.isError ? (
          <InlineError className="m-4" message={m.error_generic()} onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <p className="p-4 text-sm text-fg-muted">{m.tokens_empty()}</p>
        ) : (
          <ul>
            {query.data.map((t) => (
              <TokenRow key={t.id} token={t} onRevoke={setRevoking} />
            ))}
          </ul>
        )}
      </Panel>
      <ConfirmDialog
        isOpen={Boolean(revoking)}
        onOpenChange={(open) => !open && setRevoking(null)}
        title={m.token_revoke_title({ name: revoking?.label ?? "" })}
        description={m.token_revoke_body()}
        confirmLabel={m.token_revoke()}
        onConfirm={async () => {
          await api.delete(`/api/v1/tokens/${revoking!.id}`);
          toast.show({ title: m.token_revoked_toast(), tone: "success" });
          void qc.invalidateQueries({ queryKey: keys.tokens });
          void qc.invalidateQueries({ queryKey: keys.me });
        }}
      />
    </section>
  );
}

function Sessions() {
  const query = useQuery(sessionsQuery);
  return (
    <section aria-labelledby="sessions-title" className="flex flex-col gap-3">
      <SectionHeader id="sessions-title" title={m.sessions_title()} />
      <Panel>
        {query.isPending ? (
          <RowSkeleton rows={2} className="p-4" />
        ) : query.isError ? (
          <InlineError className="m-4" message={m.error_generic()} onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <p className="p-4 text-sm text-fg-muted">{m.sessions_empty()}</p>
        ) : (
          <ul>
            {query.data.map((s) => (
              <li
                key={s.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-4 py-3 text-sm last:border-b-0"
              >
                <span className="font-medium">{s.agentName}</span>
                <span className="text-fg-muted">{s.clientName ?? m.session_unknown_client()}</span>
                {s.projectKey ? <Badge>{s.projectKey}</Badge> : null}
                {s.repoLabel ? <span className="font-mono text-xs text-fg-muted">{s.repoLabel}</span> : null}
                <span className="ml-auto flex items-center gap-2 text-xs text-fg-subtle">
                  <Badge tone={s.endedAt ? "neutral" : "success"}>
                    {s.endedAt ? m.session_ended() : m.session_active()}
                  </Badge>
                  <RelativeTime value={s.lastSeenAt} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </section>
  );
}

function AgentsPage() {
  return (
    <Page title={m.agents_title()} intro={m.agents_intro()}>
      <ConnectClaude />
      <Tokens />
      <Sessions />
    </Page>
  );
}
